import { useCallback, useContext, useEffect, useState } from 'react';
import 'rc-slider/assets/index.css';
import SearchSource from '~/constants/SearchSource';
import AppliedFiltersContext, {
  RangePayload
} from '~/contexts/AppliedFiltersContext';
import SearchEventsDataContext from '~/contexts/SearchEventsDataContext';
import { AppliedRangeFilters } from '~/contracts';
import { RangeFacet } from '~/contracts';
import theme from '~/theme';
import currencyFormatter from '~/utilities/CurrencyFormatter';
import DebouncedInput from './DebouncedInput';
import {
  InputsWrapper,
  Range,
  RangeHeader,
  RangeLabels,
  Wrapper
} from './FacetPriceRange.styles';

type OwnProps = {
  className?: string;
  facetName: keyof AppliedRangeFilters;
  inputWidth?: number;
  isCashFormat: boolean;
  maxRange: number;
  minRange: number;
  step: number;
  title: string;
  titleExtraStyles?: string;
  values: Partial<RangeFacet>;
  withPlusLabel?: boolean;
};

export default function FacetPriceRange({
  className,
  facetName,
  inputWidth,
  isCashFormat = false,
  maxRange,
  minRange,
  step,
  title,
  titleExtraStyles = '',
  values,
  withPlusLabel = false
}: OwnProps) {
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { appliedFilters, updateRangeFilter } = useContext(
    AppliedFiltersContext
  );
  const { max, min } = values;
  const [currentMinValue, setCurrentMinValue] = useState(minRange);
  const [currentMaxValue, setCurrentMaxValue] = useState(maxRange);

  const updateFiltersRange = useCallback(
    (dataToUpdate: RangePayload) => {
      const range = appliedFilters.price;

      const {
        value: { min: currentMin, max: currentMax }
      } = dataToUpdate;
      const shouldUpdate =
        range?.min !== currentMin || range?.max !== currentMax;
      if (!shouldUpdate) {
        return;
      }

      updateRangeFilter(dataToUpdate);
      setSearchSource(SearchSource.Facet);
    },
    [appliedFilters, setSearchSource, updateRangeFilter]
  );

  const updateRanges = useCallback(
    ranges => {
      const [minValue, maxValue] = ranges;

      setCurrentMinValue(minValue || minRange);
      setCurrentMaxValue(maxValue || maxRange);
    },
    [maxRange, minRange, setCurrentMaxValue, setCurrentMinValue]
  );

  useEffect(() => {
    updateRanges([min, max]);
  }, [max, min, updateRanges]);

  const onAfterChange = () => {
    const value = { min: currentMinValue, max: currentMaxValue };
    const filterDataToUpdate: RangePayload = {
      filterKey: facetName,
      value
    };

    updateFiltersRange(filterDataToUpdate);
  };

  const updateRangesFromInput = (type, evt) => {
    const value = parseInt(evt.target.value.replace(/\D/g, ''), 10);
    const currentValues = { min: currentMinValue, max: currentMaxValue };
    const filterDataToUpdate = {
      filterKey: facetName,
      value: { ...currentValues, [type]: value }
    };

    let isValidNumber = value >= 0 && value >= minRange && value <= maxRange;

    if (type === 'max' && value < currentMinValue) {
      isValidNumber = false;
    }

    if (type === 'min' && isValidNumber) {
      setCurrentMinValue(value);
    }

    if (type === 'max' && isValidNumber) {
      setCurrentMaxValue(value);
    }

    if (isValidNumber) {
      updateFiltersRange(filterDataToUpdate);
    }
  };

  return (
    <Wrapper className={className}>
      <RangeHeader>
        <span className={`title ${titleExtraStyles}`}>{title}</span>
        <InputsWrapper>
          <DebouncedInput
            dataQa={`facet-${facetName}-min`}
            minLength={1}
            maxLength={6}
            onChange={evt => updateRangesFromInput('min', evt)}
            width={inputWidth}
            withDollarSign={isCashFormat}
            value={currentMinValue}
            focusEventType="min"
            type="number"
            pattern="\d*"
          />
          &nbsp;-&nbsp;
          <DebouncedInput
            dataQa={`facet-${facetName}-max`}
            minLength={1}
            maxLength={6}
            onChange={evt => updateRangesFromInput('max', evt)}
            withDollarSign={isCashFormat}
            width={inputWidth}
            value={currentMaxValue}
            focusEventType="max"
            type="number"
            pattern="\d*"
          />
        </InputsWrapper>
      </RangeHeader>
      <div data-qa={`facet-${facetName}`}>
        <Range
          defaultValue={[min, max]}
          handleStyle={[
            {
              borderColor: theme.carvana.blue.primary,
              height: 25,
              width: 25,
              marginTop: -10
            },
            {
              borderColor: theme.carvana.blue.primary,
              height: 25,
              width: 25,
              marginTop: -10
            }
          ]}
          min={minRange}
          max={maxRange}
          onAfterChange={onAfterChange}
          onChange={updateRanges}
          step={step}
          trackStyle={[
            { backgroundColor: theme.carvana.blue.primary },
            { backgroundColor: theme.carvana.blue.primary }
          ]}
          value={[currentMinValue, currentMaxValue]}
        />
      </div>
      <RangeLabels>
        <span>{isCashFormat ? currencyFormatter(minRange) : minRange}</span>
        <span>
          {isCashFormat
            ? `${currencyFormatter(maxRange)}`
            : `${maxRange}${withPlusLabel ? ' +' : ''}`}
        </span>
      </RangeLabels>
    </Wrapper>
  );
}
